<template>
  <div class="user-view-container">
    <!-- Header -->
    <div class="user-header mb-4">
      <div class="d-flex align-items-center">
        <button class="btn btn-outline-secondary btn-sm me-3" @click="goBack">
          <i class="fas fa-arrow-left me-2"></i>{{ $t('Back') }}
        </button>
        <h2 class="mb-0">
          <i class="fas fa-user me-2"></i>
          {{ $t('User Profile') }}
        </h2>
      </div>
    </div>

    <!-- Loading state -->
    <ContentSkeleton
      v-if="loading"
      variant="detail"
      :rows="6"
      :label="$t('Loading user profile...')"
    />

    <!-- Error state -->
    <div v-else-if="error" class="alert alert-danger">
      <i class="fas fa-exclamation-triangle me-2"></i>
      {{ error }}
    </div>

    <!-- User profile -->
    <div v-else-if="user" class="row">
      <!-- User card -->
      <div class="col-md-4">
        <div class="card shadow-sm mb-4">
          <div class="card-body text-center">
            <div class="avatar-container mb-3">
              <img 
                v-if="user.avatar" 
                :src="user.avatar" 
                class="rounded-circle user-avatar"
                alt="User avatar"
              />
              <div v-else class="avatar-placeholder rounded-circle">
                <i class="fas fa-user fa-3x"></i>
              </div>
            </div>
            <h4 class="card-title mb-1">{{ user.name }}</h4>
            <p class="text-muted mb-2">{{ user.email }}</p>
            <span 
              class="badge"
              :class="user.status === 'active' ? 'bg-success' : 'bg-secondary'"
            >
              {{ user.status === 'active' ? $t('Active') : $t('Inactive') }}
            </span>
          </div>
        </div>

        <!-- Role card -->
        <div class="card shadow-sm mb-4" v-if="user.role">
          <div class="card-header">
            <i class="fas fa-shield-alt me-2"></i>{{ $t('Role') }}
          </div>
          <div class="card-body">
            <h5 class="mb-0">
              {{ typeof user.role === 'object' ? user.role.name : user.role }}
            </h5>
          </div>
        </div>
      </div>

      <!-- User details -->
      <div class="col-md-8">
        <div class="card shadow-sm">
          <div class="card-header">
            <i class="fas fa-info-circle me-2"></i>{{ $t('User Details') }}
          </div>
          <div class="card-body">
            <div class="row">
              <!-- Name -->
              <div class="col-md-6 mb-3">
                <label class="form-label text-muted">{{ $t('Name') }}</label>
                <p class="mb-0 fw-bold">{{ user.name || '-' }}</p>
              </div>

              <!-- Email -->
              <div class="col-md-6 mb-3">
                <label class="form-label text-muted">{{ $t('Email') }}</label>
                <p class="mb-0">
                  <a :href="`mailto:${user.email}`" class="text-decoration-none">
                    {{ user.email || '-' }}
                  </a>
                </p>
              </div>

              <!-- Language -->
              <div class="col-md-6 mb-3" v-if="user.language">
                <label class="form-label text-muted">{{ $t('Language') }}</label>
                <p class="mb-0">{{ user.language }}</p>
              </div>

              <!-- Country -->
              <div class="col-md-6 mb-3" v-if="user.country">
                <label class="form-label text-muted">{{ $t('Country') }}</label>
                <p class="mb-0">{{ user.country }}</p>
              </div>

              <!-- Company Name -->
              <div class="col-md-6 mb-3" v-if="user.companyName">
                <label class="form-label text-muted">{{ $t('Company Name') }}</label>
                <p class="mb-0">{{ user.companyName }}</p>
              </div>

              <!-- Company Link -->
              <div class="col-md-6 mb-3" v-if="user.companyLink">
                <label class="form-label text-muted">{{ $t('Company Link') }}</label>
                <p class="mb-0">
                  <a :href="user.companyLink" target="_blank" class="text-decoration-none">
                    {{ user.companyLink }}
                    <i class="fas fa-external-link-alt ms-1"></i>
                  </a>
                </p>
              </div>

              <!-- Status -->
              <div class="col-md-6 mb-3">
                <label class="form-label text-muted">{{ $t('Status') }}</label>
                <p class="mb-0">
                  <span 
                    class="badge"
                    :class="user.status === 'active' ? 'bg-success' : 'bg-secondary'"
                  >
                    {{ user.status === 'active' ? $t('Active') : $t('Inactive') }}
                  </span>
                </p>
              </div>

              <!-- Created At -->
              <div class="col-md-6 mb-3" v-if="user.createdAt">
                <label class="form-label text-muted">{{ $t('Created At') }}</label>
                <p class="mb-0">{{ formatDate(user.createdAt) }}</p>
              </div>

              <!-- Last Login -->
              <div class="col-md-6 mb-3" v-if="user.lastLogin">
                <label class="form-label text-muted">{{ $t('Last Login') }}</label>
                <p class="mb-0">{{ formatDate(user.lastLogin) }}</p>
              </div>
            </div>
          </div>
        </div>

        <!-- Signature -->
        <div class="card shadow-sm mt-4" v-if="user.signature">
          <div class="card-header">
            <i class="fas fa-signature me-2"></i>{{ $t('Signature') }}
          </div>
          <div class="card-body">
            <img :src="user.signature" alt="Signature" class="img-fluid signature-img" />
          </div>
        </div>

        <!-- Actions -->
        <div class="card shadow-sm mt-4">
          <div class="card-header">
            <i class="fas fa-cog me-2"></i>{{ $t('Actions') }}
          </div>
          <div class="card-body">
            <button 
              class="btn btn-primary me-2" 
              @click="editUser"
              v-if="canEdit"
            >
              <i class="fas fa-edit me-2"></i>{{ $t('Edit User') }}
            </button>
            <button 
              class="btn btn-outline-secondary" 
              @click="goToUsersList"
            >
              <i class="fas fa-users me-2"></i>{{ $t('View All Users') }}
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Not found state -->
    <div v-else class="alert alert-warning">
      <i class="fas fa-exclamation-triangle me-2"></i>
      {{ $t('User not found') }}
    </div>
  </div>
</template>

<script>
import { wencesApi } from "@/api/wencesApi";
import permissionsMixin from "@/mixins/permissionsMixin";
import ContentSkeleton from "@/components/common/ContentSkeleton.vue";

export default {
  name: "UserView",
  components: { ContentSkeleton },
  mixins: [permissionsMixin],
  data() {
    return {
      user: null,
      loading: true,
      error: null,
    };
  },
  computed: {
    userId() {
      return this.$route.params.itemId;
    },
    canEdit() {
      return this.hasPermission("users.manage") || this.hasPermission("users.edit");
    },
  },
  async mounted() {
    await this.fetchUserPermissions();
    await this.loadUser();
  },
  watch: {
    "$route.params.itemId": {
      handler() {
        this.loadUser();
      },
    },
  },
  methods: {
    async loadUser() {
      if (!this.userId) {
        this.error = this.$t("No user ID provided");
        this.loading = false;
        return;
      }

      this.loading = true;
      this.error = null;

      try {
        const { data } = await wencesApi.get(`/api/users/${this.userId}`);
        this.user = data.data || data;
      } catch (err) {
        console.error("Error loading user:", err);
        this.error = err.response?.data?.message || this.$t("Error loading user profile");
      } finally {
        this.loading = false;
      }
    },
    formatDate(date) {
      if (!date) return "-";
      return new Date(date).toLocaleDateString(undefined, {
        year: "numeric",
        month: "long",
        day: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      });
    },
    goBack() {
      this.$router.go(-1);
    },
    editUser() {
      // Navegar a la edición del usuario si existe esa ruta
      this.$router.push(`/users`);
    },
    goToUsersList() {
      this.$router.push("/users");
    },
  },
};
</script>

<style scoped>
.user-view-container {
  padding: 20px;
}

.user-header {
  border-bottom: 1px solid #dee2e6;
  padding-bottom: 15px;
}

.avatar-container {
  display: flex;
  justify-content: center;
}

.user-avatar {
  width: 120px;
  height: 120px;
  object-fit: cover;
  border: 3px solid #dee2e6;
}

.avatar-placeholder {
  width: 120px;
  height: 120px;
  background-color: #e9ecef;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #6c757d;
}

.signature-img {
  max-height: 100px;
}

.card-header {
  font-weight: 600;
  background-color: #f8f9fa;
}

.form-label {
  font-size: 0.85rem;
  margin-bottom: 0.25rem;
}
</style>

